import React, { useRef, useState } from 'react';
import { useCMS } from '../context/CMSContext';
import { useAuth } from '../context/AuthContext';
import api, { resolveUrl } from '../api';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

// Pure helpers — extracted from the component so each function stays simple.

const uploadImageFile = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/uploads', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.url;
};

const validateUpload = (file) => {
  if (!file) return 'no-file';
  if (file.size > MAX_UPLOAD_BYTES) return 'too-large';
  return 'ok';
};

const performUpload = async ({ file, onUpload, k, saveContent }) => {
  const newUrl = await uploadImageFile(file);
  if (onUpload) await onUpload(newUrl);
  else if (k) await saveContent(k, newUrl);
};

const UploadOverlay = ({ uploading, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="absolute inset-0 flex items-center justify-center bg-slate-900/60 text-white opacity-0 hover:opacity-100 transition-opacity rounded-[inherit]"
  >
    {uploading ? (
      <span className="flex items-center gap-2 text-sm font-semibold">
        <Loader2 size={16} className="animate-spin" /> Uploading...
      </span>
    ) : (
      <span className="flex flex-col items-center gap-1 text-sm font-semibold">
        <Upload size={20} /> Click to replace
      </span>
    )}
  </button>
);

const EditableImage = ({ k, src, onUpload, alt = '', className = '', style, fallbackOnly = false }) => {
  const { t, saveContent, editMode } = useCMS();
  const { isAuthed } = useAuth();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const value = k ? (t(k, '') || src) : src;
  const resolved = resolveUrl(value);
  const editable = isAuthed && editMode && !fallbackOnly;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    const status = validateUpload(file);
    if (status === 'too-large') {
      toast.error('File too large (max 25MB). Try a smaller photo or screenshot.');
      return;
    }
    if (status !== 'ok') return;
    setUploading(true);
    try {
      await performUpload({ file, onUpload, k, saveContent });
      toast.success('Image updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className={`relative ${editable ? 'cms-editable-img' : ''}`} style={style}>
      <img src={resolved} alt={alt} className={className} />
      {editable && <UploadOverlay uploading={uploading} onClick={() => inputRef.current?.click()} />}
      {editable && (
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFile}
          className="hidden"
        />
      )}
    </div>
  );
};

export default EditableImage;
